// @ts-strict-ignore
import React, { useMemo } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { RecaptchaTokenError } from 'recaptcha/RecaptchaTokenError'
import { Box, Button, Box as FormBody, Input, InputProps } from '@node-space/ui-components'
import { logSentryErrorAndGetEventId } from '@node-space/utils'
import { RequestError } from 'components/Errors/RequestError'
import LoginPageLayout, { LoginPageLayoutVariant } from 'components/layout/LoginPageLayout'
import { PathNames } from 'constants/General'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useFormFields } from 'hooks/forms/useFormFields'
import { useLoginMutation } from 'hooks/mutations'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { PostLoginReq } from 'types/authentication/PostLoginReq'
import { BaseErrorResponse } from 'types/beneficiaries'
import { ErrorData, WorkspaceListItemId } from 'types/types'
import { LoginFormKeys, LoginSchema } from '../schemas/loginSchema'
import { LoginSharedProps } from '../types'
import { getLoginErrorType, LoginError } from '../utils'
import { LoginErrorMessage } from './LoginErrorMessage'

interface LoginFormProps extends LoginSharedProps {
  setLoginData: React.Dispatch<React.SetStateAction<PostLoginReq>>
  setShowTwoFactor: (value: boolean) => void
  setRequiresWorkspaceSelection: (workspaces: [WorkspaceListItemId]) => Promise<void>
}

const LoginForm = ({
  loginData,
  recaptcha,
  setLoginData,
  setShowTwoFactor,
  setRequiresWorkspaceSelection,
  setSuccessfulLogin,
}: LoginFormProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()

  const { isLoading: isProfileLoading } = useProfileContext()
  const { mutate: postLogin, isPending: isLoadingSubmitRequest } = useLoginMutation()

  const formSchema = LoginSchema(t)
  const form = useForm<PostLoginReq>({
    defaultValues: loginData,
    mode: 'onChange',
    resolver: yupResolver(formSchema),
  })
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors: fieldErrors, isSubmitting },
  } = form
  const formValues = useWatch({ control })

  const { setEmailField, setInputField } = useFormFields<LoginFormKeys>(
    formSchema,
    form,
    formValues
  )

  const formFields: Record<LoginFormKeys, InputProps> = {
    emailAddress: {
      ...setEmailField({ key: 'emailAddress' }),
      inputMode: 'email',
      label: t('email'),
    },
    password: {
      ...setInputField({
        key: 'password',
        type: 'password',
        placeholder: t('enterPassword'),
      }),
      inputMode: 'text',
      label: t('password'),
    },
  }

  const isLoading = useMemo(
    () => isProfileLoading || recaptcha?.isLoading || isSubmitting || isLoadingSubmitRequest,
    [isProfileLoading, recaptcha?.isLoading, isSubmitting, isLoadingSubmitRequest]
  )

  const onSubmit: SubmitHandler<typeof formValues> = async (formValues: PostLoginReq) => {
    resetRequestError()
    const recaptchaToken = await recaptcha?.getToken()

    if (!recaptchaToken) {
      setRequestError({
        handledMessage: <RecaptchaTokenError />,
        show: true,
      })
      return
    }

    const payload: PostLoginReq = {
      ...formValues,
      recaptcha: recaptchaToken,
      recaptchaVersion: recaptcha?.version,
    }

    postLogin(payload, {
      onSuccess: async response => {
        await setSuccessfulLogin(response)
      },
      onError: async (error: BaseErrorResponse) => {
        const errorCode = error?.status
        const errorResponseMessage = error?.data?.message
        const errorType = getLoginErrorType(errorCode, errorResponseMessage)
        let sentryEventId = ''

        if (errorType === LoginError.TWO_FACTOR || errorType === LoginError.WORKSPACE) {
          setLoginData(payload)
          if (errorType === LoginError.TWO_FACTOR) {
            setShowTwoFactor(true)
          }
          if (errorType === LoginError.WORKSPACE) {
            await setRequiresWorkspaceSelection(error.data as [WorkspaceListItemId] & ErrorData)
          }
          return
        }

        if (!errorType || errorType === LoginError.FORBIDDEN) {
          sentryEventId = logSentryErrorAndGetEventId(
            `Failed API request to '/login' with status ${error?.status}${
              errorResponseMessage ? ` - ${errorResponseMessage}` : ''
            }`,
            error,
            { ...payload, function: 'postLogin' },
            'fatal'
          )
        }

        setRequestError({
          errorCode,
          handledMessage: !!errorType ? <LoginErrorMessage errorType={errorType} /> : '',
          sentryEventId,
          show: true,
        })
      },
    })
  }

  return (
    <LoginPageLayout
      title={t('logInYourAccount')}
      hasFooter
      variant={LoginPageLayoutVariant.NARROW}
    >
      {requestError?.show && (
        <RequestError
          errorCode={requestError?.errorCode}
          fallbackMessage={t('errorMessages.login.default')}
          sentryTraceId={requestError?.sentryEventId}
        >
          {requestError?.handledMessage}
        </RequestError>
      )}
      <form onSubmit={handleSubmit(onSubmit)} className="w-full">
        <FormBody flexCol gap={8}>
          <Box flexCol gap={24}>
            <Input
              {...formFields.emailAddress}
              testId="emailAddress"
              onChange={e => {
                setValue('emailAddress', e?.target?.value, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }}
            />
            <Input
              {...formFields.password}
              testId="password"
              onChange={e => {
                setValue('password', e?.target?.value, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }}
            />
            <Button
              type="submit"
              loading={isLoading}
              disabled={!!Object.keys(fieldErrors)?.length}
              full
              testId="loginButton"
              className="mt-1"
            >
              {`${t('logIn')}`}
            </Button>
          </Box>
          <Button onClick={() => navigate(PathNames.FORGOT_PASSWORD)} variant="ghost">
            {t('forgotPassword')}
          </Button>
        </FormBody>
      </form>
    </LoginPageLayout>
  )
}

export default LoginForm
